import type { Chain } from '../features/catalog/api';

/**
 * Phần thuần dữ liệu của khối mã khuyến mãi KAS (`S4-Web-05`).
 *
 * Tách khỏi `KasPromoCodeFields.tsx` vì một file `.tsx` vừa export component vừa export hàm thuần
 * sẽ làm hot-reload mất trạng thái (`react-refresh/only-export-components`).
 */

/** Một mã đã khai, đọc lại từ server. */
export interface KasPromoCodeEntry {
  chainId: string;
  code: string;
}

/** Trần độ dài của cột lưu mã. Giữ khớp để người khai chạm trần ngay lúc gõ, không phải lúc bấm Lưu. */
export const KAS_CODE_MAX = 64;

/** Những chuỗi đang trong phạm vi: chọn một chuỗi thì đúng chuỗi đó, bỏ trống thì toàn bộ. */
export function kasChainsInScope(chains: Chain[], chainId: string | null | undefined): Chain[] {
  return chainId ? chains.filter((c) => c.id === chainId) : chains;
}

/** Dựng `Record<chainId, code>` để đổ vào form từ dữ liệu server trả về. */
export function toKasFormValue(entries?: KasPromoCodeEntry[] | null): Record<string, string> {
  const map: Record<string, string> = {};
  for (const entry of entries ?? []) map[entry.chainId] = entry.code;
  return map;
}

/**
 * Dựng mảng gửi lên từ giá trị form.
 *
 * 🔴 **Lọc theo phạm vi HIỆN TẠI, không gửi cả map.** Server thay thế trọn bộ: chuỗi nào không có
 * trong mảng thì mã của nó bị xoá. Nhờ vậy đổi phạm vi từ toàn hệ thống về một chuỗi sẽ kéo theo mã
 * của những chuỗi bị bỏ ra — gửi cả map thì mã cũ nằm lại, và không ai thấy cho tới lúc nó hiện ra
 * ở quầy cho thu ngân gõ.
 *
 * Luôn trả về mảng, **không bao giờ `null`**: `null` nghĩa là "không đụng tới", nên gửi `null` ở đây
 * sẽ gây đúng cái sót mã mà hàm này sinh ra để chặn.
 */
export function toKasPayload(
  value: Record<string, string> | undefined,
  chainId: string | null | undefined,
  chains: Chain[],
): { chainId: string; code: string }[] {
  const map = value ?? {};

  return kasChainsInScope(chains, chainId).map((chain) => ({
    chainId: chain.id,
    // Mã rỗng là hợp lệ và có nghĩa: xoá mã của chuỗi đó.
    code: (map[chain.id] ?? '').trim(),
  }));
}

/**
 * Những chuỗi trong phạm vi mà **chưa** có mã — dựng cột cảnh báo trên danh sách.
 *
 * Mã là tuỳ chọn nên thiếu mã không phải lỗi. Nhưng khai xong rồi quên thì chỉ tới lúc thu ngân
 * đứng trước khách mới biết; cột này bắt đúng chỗ đó trước khi ra quầy, và gần như không tốn thêm
 * công vì dữ liệu đã nằm sẵn trong danh sách.
 */
export function missingKasChains(
  entries: KasPromoCodeEntry[] | null | undefined,
  chainId: string | null | undefined,
  chains: Chain[],
): string[] {
  const declared = new Set(
    (entries ?? []).filter((e) => e.code.trim() !== '').map((e) => e.chainId),
  );

  return kasChainsInScope(chains, chainId)
    .filter((c) => !declared.has(c.id))
    .map((c) => c.name);
}
